import { getNbDecimal, roundDecimal } from "components/helper/numberHelper";
import { Dispatch, useEffect, useReducer } from "react";
import { QuestionType } from "types/dataVoteType";
import { VoteStatus } from "types/voteEnum";
import {
    createInitialAnswerStatus,
    hasPersonAlreadyAnswered,
    isAnswerLocked,
    rollbackLockedVotes,
} from "components/vote/common/answerLock";

type Answer = {
    id: string;
    weight: number;
    disableOtherPropositionsIfSelected: boolean;
};

export type VoteState = {
    voterId: string;
    voteStatus: VoteStatus;
    answers: Array<Answer>;
};

type State = Array<VoteState>;

export type Person = {
    displayName: string;
    id: string;
    weight: number;
};

export type VoteStateAction =
    | { type: "updateWeight"; id: string; weight: number; voterId: string }
    | {
          type: "replaceVoteForEveryParticipant";
          id: string;
          weight: number;
      }
    | { type: "sendAnswersRequest"; voterId: string }
    | { type: "sendAnswersSuccess"; voterId: string }
    | { type: "sendAnswersError"; voterId: string }
    | { type: "resetVote"; voterId: string }
    | { type: "initializeVotes" }
    | { type: "resetEveryOneVotes" }
    | { type: "resetProxyVotes" };

type ReducerAction = Exclude<VoteStateAction, { type: "initializeVotes" }>;

function useVoteState(
    question: QuestionType,
    proxies: Array<Person>,
    user: Person,
) {
    function reducer(state: State, action: ReducerAction): State {
        switch (action.type) {
            case "updateWeight": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        const voter = [user, ...proxies].find(
                            (person) => person.id === vote.voterId,
                        ) as Person;

                        const currentVoteShouldDisabledOtherPropositions =
                            (vote.answers.find(
                                (answer) => action.id === answer.id,
                            )?.disableOtherPropositionsIfSelected as boolean) &&
                            /* Useful only in case of a vote with distribution
                               We don't want to disable other propositions if the weight is not the total weight */
                            action.weight === voter.weight;

                        const newAnswers = vote.answers.map((answer) => {
                            if (answer.id === action.id) {
                                return { ...answer, weight: action.weight };
                            }

                            return currentVoteShouldDisabledOtherPropositions
                                ? { ...answer, weight: 0 }
                                : answer;
                        });

                        const distributedWeight = newAnswers.reduce(
                            (prev, curr) =>
                                roundDecimal(
                                    prev + curr.weight,
                                    getNbDecimal(voter.weight),
                                ),
                            0,
                        );

                        const numberOfActiveAnswers = newAnswers.filter(
                            (answer) => answer.weight,
                        ).length;

                        const getNewVoteStatus = () => {
                            if (distributedWeight >= voter.weight) {
                                if (
                                    currentVoteShouldDisabledOtherPropositions
                                ) {
                                    return VoteStatus.PendingValidation;
                                }

                                if (
                                    numberOfActiveAnswers <
                                    question.minNumberOfAnswers
                                ) {
                                    return VoteStatus.Incomplete;
                                }
                                return VoteStatus.PendingValidation;
                            } else if (0 === distributedWeight) {
                                return VoteStatus.Idle;
                            } else {
                                return VoteStatus.Incomplete;
                            }
                        };

                        return {
                            ...vote,
                            voteStatus: getNewVoteStatus(),
                            answers: newAnswers,
                        };
                    }),
                ];
            }
            case "replaceVoteForEveryParticipant": {
                return [
                    ...state.map((vote) => {
                        const voter = [user, ...proxies].find(
                            (person) => person.id === vote.voterId,
                        ) as Person;

                        if (0 === voter.weight) {
                            return vote;
                        }

                        const currentVoteShouldDisabledOtherPropositions =
                            vote.answers.find(
                                (answer) => action.id === answer.id,
                            )?.disableOtherPropositionsIfSelected as boolean;
                        const newAnswers = vote.answers.map((answer) => {
                            if (answer.id === action.id) {
                                return {
                                    ...answer,
                                    weight:
                                        // In case of unselect proposal the weight is 0
                                        // In case of select proposal the weight is the total weight of user
                                        action.weight === 0
                                            ? action.weight
                                            : voter.weight,
                                };
                            }
                            return currentVoteShouldDisabledOtherPropositions
                                ? { ...answer, weight: 0 }
                                : answer;
                        });
                        const distributedWeight = newAnswers.reduce(
                            (previousValue, currentValue) =>
                                previousValue + currentValue.weight,
                            0,
                        );

                        const numberOfActiveAnswers = newAnswers.filter(
                            (answer) => answer.weight,
                        ).length;

                        const getNewVoteStatus = () => {
                            if (distributedWeight >= voter.weight) {
                                if (
                                    currentVoteShouldDisabledOtherPropositions
                                ) {
                                    return VoteStatus.PendingValidation;
                                }

                                if (
                                    numberOfActiveAnswers <
                                    question.minNumberOfAnswers
                                ) {
                                    return VoteStatus.Incomplete;
                                }

                                return VoteStatus.PendingValidation;
                            } else if (0 === distributedWeight) {
                                return VoteStatus.Idle;
                            } else {
                                return VoteStatus.Incomplete;
                            }
                        };

                        return {
                            ...vote,
                            voteStatus: getNewVoteStatus(),
                            answers: newAnswers,
                        };
                    }),
                ];
            }
            case "sendAnswersRequest": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        return {
                            ...vote,
                            voteStatus: VoteStatus.Loading,
                        };
                    }),
                ];
            }
            case "sendAnswersSuccess": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        return {
                            ...vote,
                            voteStatus: VoteStatus.Success,
                        };
                    }),
                ];
            }
            case "sendAnswersError": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        return {
                            ...vote,
                            voteStatus: VoteStatus.Error,
                        };
                    }),
                ];
            }
            case "resetVote": {
                return [
                    ...state.map((vote) => {
                        if (vote.voterId !== action.voterId) {
                            return vote;
                        }

                        const voter = [user, ...proxies].find(
                            (person) => person.id === vote.voterId,
                        ) as Person;

                        return {
                            ...vote,
                            answers: createInitialAnswers(
                                question.propositions,
                            ),
                            voteStatus: createInitialVoteStatus(voter.weight),
                        };
                    }),
                ];
            }
            case "resetEveryOneVotes": {
                return [
                    ...state.map((vote) => {
                        const voter = [user, ...proxies].find(
                            (person) => person.id === vote.voterId,
                        ) as Person;

                        return {
                            ...vote,
                            voteStatus: createInitialVoteStatus(voter.weight),
                            answers: createInitialAnswers(
                                question.propositions,
                            ),
                        };
                    }),
                ];
            }
            case "resetProxyVotes": {
                return state.map((vote) => {
                    const proxy = proxies.find(
                        (proxy) => proxy.id === vote.voterId,
                    );

                    if (!proxy) {
                        return vote;
                    }

                    return {
                        ...vote,
                        answers: createInitialAnswers(question.propositions),
                        voteStatus: createInitialVoteStatus(proxy.weight),
                    };
                });
            }
        }
    }

    function isVoteLocked(vote: VoteState): boolean {
        return isAnswerLocked(
            vote.voteStatus,
            hasPersonAlreadyAnswered(vote.voterId, question.answeredVoterIds),
            question.canAnswerBeUpdated,
        );
    }

    // Single lock point: once a voter's answer is final, every action the
    // reducer computed for them is rolled back.
    function reducerWithAnswerLock(
        state: State,
        action: VoteStateAction,
    ): State {
        if ("initializeVotes" === action.type) {
            return createInitialState(question, proxies, user);
        }

        return rollbackLockedVotes(state, reducer(state, action), isVoteLocked);
    }

    const [state, dispatch] = useReducer(
        reducerWithAnswerLock,
        createInitialState(question, proxies, user),
    );

    // Only the question identity and the answers already recorded for THESE
    // participants may reset the state: keying on the whole question (or on
    // every voter's answers) would wipe a vote in progress each time someone
    // else votes.
    const answeredParticipantsKey = [user, ...proxies]
        .filter((person) =>
            hasPersonAlreadyAnswered(person.id, question.answeredVoterIds),
        )
        .map((person) => person.id)
        .join("|");

    useEffect(() => {
        // This check is performed to handle cases where a user reconnects
        // via a mobile socket without being notified of the closure of the
        // previous vote. We add question as dependency in order
        // to determine if the vote state needs to be updated. If the titles
        // has changed, it means the question has changed, and we need to
        // reset reducer state so as to reset voting state
        dispatch({ type: "initializeVotes" });
    }, [question.id, answeredParticipantsKey]);

    return [state, dispatch] as [State, Dispatch<VoteStateAction>];
}

type Proposal = {
    id: string;
    disableOtherPropositionsIfSelected: boolean;
};

function createInitialAnswers(proposals: Array<Proposal>): Array<{
    id: string;
    weight: number;
    disableOtherPropositionsIfSelected: boolean;
}> {
    return proposals.map((proposal) => ({
        id: proposal.id,
        weight: 0,
        disableOtherPropositionsIfSelected:
            proposal.disableOtherPropositionsIfSelected,
    }));
}

function createInitialVoteStatus(
    personWeight: number,
    hasAlreadyAnswered: boolean = false,
): VoteStatus {
    if (0 === personWeight) {
        return VoteStatus.NotAllowed;
    }

    return createInitialAnswerStatus(hasAlreadyAnswered);
}

function createInitialState(
    question: QuestionType,
    proxies: Array<Person>,
    user: Person,
): State {
    return [user, ...proxies].map((person) => ({
        voterId: person.id,
        voteStatus: createInitialVoteStatus(
            person.weight,
            hasPersonAlreadyAnswered(person.id, question.answeredVoterIds),
        ),
        answers: createInitialAnswers(question.propositions),
    }));
}

export default useVoteState;
